/*
 * @Author: 李祥悦
 * @Date: 2024-07-11
 * @Description: 统计图浮窗
 */
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { IColumn, IEchartsTooltipProps, ITableOption } from './type';
import './index.scss';

export const getEchartTooltipString = (props: IEchartsTooltipProps) => {
  const { style, className, table, direction = 'column' } = props;

  // 表头转换
  const formatColumns = (columns: IColumn[]) => {
    const columnList = columns?.map((item) => {
      const { dataFormatRule, render, ...opt } = item;
      const column: IColumn = {
        ...opt,
        render
      };
      const { showShareLessValue,key: columnKey } = column || {};
      // 数据值格式化
      if (dataFormatRule && typeof render !== 'function') {
        column.render = (val) => {
          // 针对特殊数据不需要展示
          if (val === '__hide__') {
            return '';
          }
          if (isNaN(Number(val)) || val === '') {
            return '-';
          }
          const value = dataFormat({
            value: val,
            formatRule: dataFormatRule,
          });
          if(!!(showShareLessValue?.value)) {
            if(val !=='' && Number(val)  < showShareLessValue?.value) {
              return showShareLessValue?.text;
            }
          }
          return value;
        };
      }
      return column;
    });
    return columnList;
  };

  // 数据转换
  const formatData = (data: any[], tableOpt: ITableOption) => {
    const {
      sortBy,
      isHideNull,
      hideKey = ['value'],
      beforeData,
      afterData,
      sortType = 'desc',
    } = tableOpt;

    let newData = data || [];
    // 排序
    if (sortBy) {
      newData?.sort((a: any, b: any) =>
        sortType === 'desc'
          ? b?.[sortBy] - a?.[sortBy]
          : a?.[sortBy] - b?.[sortBy],
      );
    }
    // 过滤空值
    if (isHideNull) {
      newData = newData?.filter((item: any) =>
        hideKey?.every((key) => !!item?.[key]),
      );
    }
    // 增加前置数据
    if (beforeData && beforeData?.length > 0) {
      newData?.unshift(...beforeData);
    }
    // 增加后置数据
    if (afterData && afterData?.length > 0) {
      newData?.push(...afterData);
    }
    return newData;
  };

  const newTable = Array.isArray(table) ? table : [table];

  const dom = (
    <div
      className={`dbd-echarts-tooltip-container ${className || ''} ${direction}`}
      style={style}
    >
      {newTable?.map((tableOpt, index) => {
        const { columns, className, style, data, hideHeader, title } = tableOpt || {};
        const newColumns = formatColumns(columns);
        const newData = formatData(data, tableOpt);
        return (
          <div key={index}>
            {title && <div className='dbd-echarts-tooltip-container-title'>{title}</div>}
            <table
              className={`dbd-echarts-tooltip-container-table ${className}`}
              style={style}
            >
              {!hideHeader && (
                <thead>
                  <tr>
                    {newColumns?.map((item) => (
                      <th key={item?.key} align={item?.align || 'left'} style={{ width: item.width }}>
                        {item?.header || ''}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {newData.map((item, index) => {
                  const { iconType: dataIconType, color } = item || {};
                  return (
                    <tr key={index}>
                      {newColumns?.map((column) => {
                        const {
                          key,
                          align,
                          showIcon,
                          iconType = 'circular',
                          style: colStyle,
                          render,
                        } = column || {};

                        // 有render时传入icon方便自行处理
                        const iconDom = showIcon && color && (
                          <span
                            className={`dbd-echarts-tooltip-container-icon ${dataIconType || iconType}`}
                            style={{ background: color }}
                          ></span>
                        );

                        return (
                          <td key={key} align={align || 'left'} style={colStyle}>
                            {render
                              ? render(item[key], item, column, iconDom)
                              : <>{iconDom}{item[key]}</>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );

  return ReactDOMServer.renderToString(dom);
};
